import { LIBRARY_SLUG_PATTERN, libraryRightBelongsTo, libraryRightNameSchema } from "./library-rights.js";
import { z } from "zod";
import { utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/library/library-use.ts
/**
* The use of one shared library by a consuming app or a consuming library: the file
* `libraries/<slug>/library.json` in its source tree (2026-10-12-shared-libraries, D8, D9, D10,
* R-013, R-014, R-033).
*
* The file names the library, a semver range, the library rights the app requests and the values
* of the library's `consumerConfig`. `zion-app release` (WS-12) carries each file as one bundle layer
* of media type `LIBRARY_USE_LAYER_MEDIA_TYPE`. The CP publish door (WS-2) parses each layer with
* {@link libraryUseSchema}, resolves the range with `semver` (DEF-13), checks the rights against the
* library's `libraryRights[]` and checks `config` against the `consumerConfig` schema of every
* library version the range can resolve to (R-023).
*
* Three artifacts state this shape and a test pins each pair: this module (the type and the zod
* form), `schemas/library-use.schema.json` (the Ajv form) and the media type in `oci-bundle.ts`.
* The JSON Schema cannot hold three rules, the right prefix, the config depth and the config size;
* the zod form holds them.
*
* Pure and browser-safe.
*/
/** The directory of the app source tree that holds one sub-directory per library use (D9). */
const LIBRARY_USES_DIRECTORY = "libraries";
/** The file name of a library use inside `libraries/<slug>/` (D9). */
const LIBRARY_USE_FILE_NAME = "library.json";
/**
* The relative path of a library use file, with the library slug in group 1:
* `libraries/smoke-lib/library.json`. The slug part matches {@link LIBRARY_SLUG_PATTERN}; callers
* check the slug length after the match.
*/
const LIBRARY_USE_FILE_PATH_PATTERN = /^libraries\/([a-z](?:[a-z0-9]|-[a-z0-9])+)\/library\.json$/;
/** The most library uses one app or library version declares. */
const LIBRARY_USES_MAX = 16;
/**
* The most library levels below an app (R-033 item 5): an app uses L1, L1 uses L2, L2 uses L3. The
* publish door refuses a fourth level and any cycle; resolution walks at most this many levels.
*/
const LIBRARY_CHAIN_MAX_DEPTH = 3;
/** The longest semver range text of a library use. */
const LIBRARY_RANGE_MAX_LENGTH = 128;
/**
* The characters a semver range may hold: digits, ASCII letters, `.`, `^`, `~`, `<`, `>`, `=`, `|`,
* `*`, `+`, `-` and the space. `^1.4.0`, `>=1.4 <2` and `1.x || 2.x` match. The publish door parses
* the range with `semver` (DEF-13); this grammar only bounds the text.
*/
const LIBRARY_RANGE_CHARACTERS_PATTERN = /^[0-9A-Za-z.^~<>=|*+ -]+$/;
/** A range must hold at least one digit, `x`, `X` or `*`, so a range of only operators and spaces fails. */
const LIBRARY_RANGE_VERSION_TOKEN_PATTERN = /[0-9xX*]/;
/** The largest `config` value of a library use: 65,536 bytes of UTF-8 `JSON.stringify` text. */
const LIBRARY_USE_CONFIG_MAX_BYTES = 65536;
/**
* The deepest `config` value of a library use: 32 nested JSON containers, the `config` object itself
* at depth 1, as `checkDpConfigSchema` counts. The cap is twice `DP_CONFIG_SCHEMA_MAX_DEPTH` (16).
* A deeper value gets a refusal at its path (DEF-3). An admitted value is shallow, so a later
* `JSON.stringify` of it never exhausts the stack (the operator file mount, the CP check against
* the `consumerConfig` schema).
*/
const LIBRARY_USE_CONFIG_MAX_DEPTH = 32;
/** Whether `JSON.stringify` drops `value` as an object member: `undefined`, a function or a symbol. */
function droppedAsMember(value) {
	return value === void 0 || typeof value === "function" || typeof value === "symbol";
}
/**
* Whether `value` nests at most {@link LIBRARY_USE_CONFIG_MAX_DEPTH} containers and holds at most
* {@link LIBRARY_USE_CONFIG_MAX_BYTES} written values, the root included. Every value that
* `JSON.stringify` writes takes at least one byte, so a value over the member budget is also over
* the byte cap. The walk keeps its own stack, holds only containers on it, and stops at the first
* cap it passes, so its memory stays bounded by the caps and not by the size of `value`
* (S1 contracts re-judge `architecture-2`). It reads own enumerable members, as `JSON.stringify`
* does: every array element counts (a hole and a dropped value write `null`), and an object member
* counts unless `JSON.stringify` drops it ({@link droppedAsMember}).
*/
function withinConfigShape(value) {
	let written = 1;
	const stack = [];
	if (typeof value === "object" && value !== null) stack.push([value, 1]);
	for (let next = stack.pop(); next !== void 0; next = stack.pop()) {
		const [item, depth] = next;
		if (depth > 32) return false;
		if (Array.isArray(item)) {
			written += item.length;
			if (written > 65536) return false;
			for (let i = 0; i < item.length; i += 1) {
				const child = item[i];
				if (typeof child === "object" && child !== null) stack.push([child, depth + 1]);
			}
			continue;
		}
		for (const key in item) {
			if (!Object.hasOwn(item, key)) continue;
			const child = item[key];
			if (droppedAsMember(child)) continue;
			written += 1;
			if (written > 65536) return false;
			if (typeof child === "object" && child !== null) stack.push([child, depth + 1]);
		}
	}
	return true;
}
/**
* Whether a library config value is inside both caps: at most {@link LIBRARY_USE_CONFIG_MAX_DEPTH}
* nested containers, then at most {@link LIBRARY_USE_CONFIG_MAX_BYTES} bytes of UTF-8
* `JSON.stringify` text. A walk runs first and answers `false` for a value deeper than the depth cap
* or with more than 65,536 written values (one byte each at least), so the byte count never
* stringifies a value that is too deep or too wide, and the walk stops at the cap. A cycle is
* deeper than the cap. A value that `JSON.stringify` cannot write (a `bigint`, a throwing `toJSON`)
* answers `false`. The walk reads the value's own members, so a `toJSON` that hides more than
* 65,536 of them also answers `false` (DEF-3). The three config readers call it:
* {@link libraryUseSchema} (`config`), `consumerConfigFileSchema` (`values`) and the
* `consumerConfig` of `AppLibraryDesiredStateSchema` in `@zion/sdk-cp-dp-contracts`. Never throws.
* Pure.
*/
function isLibraryConfigWithinBounds(value) {
	try {
		return withinConfigShape(value) && utf8ToBytes(JSON.stringify(value)).length <= 65536;
	} catch {
		return false;
	}
}
/**
* The zod form of {@link LibraryUse}. Strict at the root. It refuses:
*
* 1. a slug off {@link LIBRARY_SLUG_PATTERN} or over 30 characters;
* 2. an empty range, one over 128 characters, one with a character outside
*    {@link LIBRARY_RANGE_CHARACTERS_PATTERN}, or one with no version token;
* 3. a right off the library right grammar, a duplicate right, more than 32 rights, and a right
*    whose prefix is not `slug` (path `rights.<index>`);
* 4. a `config` that is not a JSON object, that nests more than 32 containers, or whose
*    `JSON.stringify` text is over 65,536 bytes ({@link isLibraryConfigWithinBounds}).
*
* It does not resolve the range and does not check `config` against a schema; the publish door
* does both.
*/
const libraryUseSchema = z.strictObject({
	$schema: z.string().max(512).optional(),
	slug: z.string().max(30).regex(LIBRARY_SLUG_PATTERN),
	range: z.string().min(1).max(128).regex(LIBRARY_RANGE_CHARACTERS_PATTERN).regex(LIBRARY_RANGE_VERSION_TOKEN_PATTERN),
	rights: z.array(libraryRightNameSchema).max(32).refine((rights) => new Set(rights).size === rights.length, { message: "rights must be unique" }).optional(),
	config: z.record(z.string(), z.unknown()).refine(isLibraryConfigWithinBounds, { message: `config exceeds 32 levels or ${LIBRARY_USE_CONFIG_MAX_BYTES} bytes` }).optional()
}).superRefine((use, ctx) => {
	(use.rights ?? []).forEach((right, index) => {
		if (!libraryRightBelongsTo(right, use.slug)) ctx.addIssue({
			code: "custom",
			path: ["rights", index],
			message: `library right ${right} does not start with ${use.slug}:`
		});
	});
});
/**
* The zod form of every library use of one app or library version: the parsed content of all its
* `libraries/<slug>/library.json` files, in any order. Each member must pass {@link libraryUseSchema}.
* It refuses:
*
* 1. more than {@link LIBRARY_USES_MAX} (16) uses;
* 2. two uses with the same `slug` (path `<index>.slug` of the second);
* 3. more than `LIBRARY_RIGHTS_CLAIM_MAX` (64) requested rights over all the uses (path `(root)`).
*
* Rule 3 exists because the `lrt` claim of a rights certificate holds at most 64 names. Each file
* may request 32 rights, so 16 files could request 512, and the certificate issuer could then sign
* no certificate for the version. The publish door (WS-2) parses the uses of a version with this
* schema, so the developer learns the limit at publish. Rights of two libraries never share a name,
* because each name starts with its library slug, so the count is the sum of the `rights` lengths.
*/
const libraryUseListSchema = z.array(libraryUseSchema).max(16).superRefine((uses, ctx) => {
	const seen = /* @__PURE__ */ new Set();
	uses.forEach((use, index) => {
		if (seen.has(use.slug)) ctx.addIssue({
			code: "custom",
			path: [index, "slug"],
			message: `library ${use.slug} is used twice`
		});
		seen.add(use.slug);
	});
	const total = uses.reduce((sum, use) => sum + (use.rights?.length ?? 0), 0);
	if (total > 64) ctx.addIssue({
		code: "custom",
		path: [],
		message: `the library uses request ${total} rights; a version requests at most 64`
	});
});
/**
* Parses untrusted `library.json` content with {@link libraryUseSchema}. A failure lists one issue
* path per broken rule, each joined with `.`, and `(root)` for the file as a whole. Never throws.
* Pure.
*/
function parseLibraryUse(value) {
	const parsed = libraryUseSchema.safeParse(value);
	if (parsed.success) return {
		ok: true,
		use: parsed.data
	};
	return {
		ok: false,
		issues: parsed.error.issues.map((issue) => issue.path.length === 0 ? "(root)" : issue.path.map(String).join("."))
	};
}
/**
* The relative path `libraries/<slug>/library.json` of the use of `librarySlug`. It throws
* `TypeError` for a slug off {@link LIBRARY_SLUG_PATTERN} or over 30 characters. Pure.
*/
function libraryUseFilePath(librarySlug) {
	if (librarySlug.length > 30 || !LIBRARY_SLUG_PATTERN.test(librarySlug)) throw new TypeError(`libraryUseFilePath: ${JSON.stringify(librarySlug)} is not a library slug`);
	return `${LIBRARY_USES_DIRECTORY}/${librarySlug}/${LIBRARY_USE_FILE_NAME}`;
}
/**
* The library slug a relative path names, or `null` when the path is no library use file path.
* `librarySlugOfUseFilePath('libraries/smoke-lib/library.json')` is `'smoke-lib'`. Never throws.
* Pure.
*/
function librarySlugOfUseFilePath(path) {
	const slug = LIBRARY_USE_FILE_PATH_PATTERN.exec(path)?.[1];
	return slug !== void 0 && slug.length <= 30 ? slug : null;
}
//#endregion
export { LIBRARY_CHAIN_MAX_DEPTH, LIBRARY_RANGE_CHARACTERS_PATTERN, LIBRARY_RANGE_MAX_LENGTH, LIBRARY_RANGE_VERSION_TOKEN_PATTERN, LIBRARY_USES_DIRECTORY, LIBRARY_USES_MAX, LIBRARY_USE_CONFIG_MAX_BYTES, LIBRARY_USE_CONFIG_MAX_DEPTH, LIBRARY_USE_FILE_NAME, LIBRARY_USE_FILE_PATH_PATTERN, isLibraryConfigWithinBounds, librarySlugOfUseFilePath, libraryUseFilePath, libraryUseListSchema, libraryUseSchema, parseLibraryUse };

//# sourceMappingURL=library-use.js.map